import {IClipsFullscreenAttrs} from "./ClipsFullscreenAttrs";
import {action, observable} from "mobx";
import {WebApi} from "@ok/web/modules/webapi/WebApi";
import {
    AnimationType,
    Clip,
    CLIP_URLS,
    ClipInfo,
    Direction,
    FullScreenItem,
    FullscreenItemType,
    GetClipsResponse,
    Marker,
    RawFullscreenItem,
    Response,
    ToastType
} from "./types";
import {LinkHelper, UpdateType} from "./helpers/LinkHelper";
import {TmpLikeHelper} from "./helpers/TmpLikeHelper";
import {append} from "OK/modules/player/oneVideoCallbacks";
import {buildUrl} from "@ok/utils/urls";
import {
    DELETION_TIMEOUT,
    DESKTOP_MARGIN_FROM_EDGE,
    ITEMS_LIST_BOUNCING_ANIMATION_MAX_ITERATION,
    OFFSET_FOR_BOUNCING_ANIMATION,
    SCROLL_ANIMATION_TIMING
} from "../config";
import {Env} from "@ok/common/utils/env";

export class ClipsFullscreenStore {
    public vkVideoPlayerBundleUrl;
    public preload = "";
    public scrollDirection: Direction = Direction.FORWARD;
    public deleteTimeout: ReturnType<typeof setTimeout> | null = null;
    public isDeletionTimeoutRunning = false;
    public toastTimeout: ReturnType<typeof setTimeout> | null = null;

    public anonymUrl: string;
    public isAnonym: boolean;
    public returnUrl: string;
    public isHandleSystemBackFeatureEnabled: boolean;
    public statesForDisableSystemBackHandling: string[] | null;
    public isDisableSystemBackHandling = false;
    public isViaUnionObserverFeature: boolean;

    public linkHelper: LinkHelper;
    public tmpLikeHelper: TmpLikeHelper;

    public isIOS: boolean;
    public isDesktop: boolean;

    public marker: Marker | null = null;
    public initTarget: HTMLDivElement | null = null;

    public isInitialThemeDark: boolean | null = null;
    public initialPlace: string | null = null;

    public initialClipMovieId: number | null = null;

    public indexToDeleteNotAllowed: number | null = null;


    public itemsListBouncingAnimationInterval: ReturnType<typeof setInterval> | null = null;
    public isItemsListBounceAnimationDirectionForward = false;
    public isItemsListBounceAnimationFirstBounce = true;
    public itemsListBouncingAnimationIterationCount = 0;

    public isFullscreenClosedViaSystemBack = false;
    public needToHandleSystemBack = true;

    public unionObserver: IntersectionObserver | null = null;

    @observable
    public indexToDelete = -1;

    @observable
    public isFullscreenOpen = false;

    @observable
    public isVisible = false;
    public animationType: AnimationType;


    @observable
    public currentInnerHeight = 0;

    @observable
    public currentOffset: number;


    @observable
    public clipsProgressPositions: Record<string, {
        currentTime: number;
        duration: number;
        timePercent: number | null;
        position: number | null;
        attempt: number;
        movieId: number;
    }> = {};

    @observable
    public rawFullscreenItems: RawFullscreenItem[] = [];

    @observable
    public currentFullscreenItems: FullScreenItem[] = [];

    @observable
    public currentItemIndex = 0;


    @observable
    public toastShow = false;
    public toastType: ToastType | null = null;

    @observable
    public isLoading = false;

    @observable
    public isLoadError = false;


    @observable
    public isScrollAnimation = true;

    @observable
    public isAnimationRunning = false;

    public scrollAnimationTiming;

    @observable
    public currentTimelineProgress: {
        duration: number;
        currentTime: number;
        movieId: number | null;
    } = {
            duration: 0,
            currentTime: 0,
            movieId: null
        };
    @observable
    public isNavigationRunning = false;

    @observable
    public isStopCurrentClip = false;

    @observable
    public hasMore = true;

    @observable
    public animateNotAllowedClip = false;

    constructor(props: IClipsFullscreenAttrs) {
        this.isFullscreenOpen = props.openState;
        this.isVisible = props.openState;
        this.animationType = props.openState ? AnimationType.INSTANT : AnimationType.FADE_IN;
        this.vkVideoPlayerBundleUrl = props.vkVideoPlayerBundleUrl;
        this.anonymUrl = props.anonymUrl;
        this.returnUrl = props.returnUrl;
        this.isIOS = props.isIOS;
        this.isDesktop = props.isDesktop;
        this.isAnonym = props.isAnonym;
        this.isHandleSystemBackFeatureEnabled = props.isHandleSystemBackFeatureEnabled;
        this.statesForDisableSystemBackHandling = props.statesForDisableSystemBackHandling;
        this.isViaUnionObserverFeature = props.isViaUnionObserverFeature;

        this.linkHelper = new LinkHelper({
            isHandleSystemBackFeatureEnabled: props.isHandleSystemBackFeatureEnabled,
            statesForDisableSystemBackHandling: props.statesForDisableSystemBackHandling
        });
        this.tmpLikeHelper = new TmpLikeHelper();

        this.setCurrentWindowInnerHeight();
        this.currentOffset = 0;

        this.scrollAnimationTiming = SCROLL_ANIMATION_TIMING.DEFAULT;

        try {
            let raw = JSON.parse(props.preload);

            if (raw[0].isOK) {
                this.rawFullscreenItems = raw;

                const clip = this.rawFullscreenItems[this.rawFullscreenItems.length - 1];
                if (clip) {
                    this.setHasMore(clip.hasMore);
                }
            }
            // eslint-disable-next-line
        } catch (e) {

        }

        if (!Env.isServerRendering) {
            append("clipsFullscreen", {
                onError: () => {
                    if (!this.isFullscreenOpen) {
                        return;
                    }

                    this.setIsLoadError(true);
                },
                //@ts-ignore
                onTimeupdate: (duration: number, position: number, movieId: number) => {
                    if (!this.isFullscreenOpen) {
                        return;
                    }

                    if (!Number.isFinite(duration)) {
                        return;
                    }

                    if (duration === 0) {
                        return;
                    }
                    this.setCurrentTimelineProgress(duration, position, movieId);
                }
            });
        }


        this.updateCurrentFullscreenItems();
    }

    @action
    setHasMore(state: boolean) {
        this.hasMore = state;
    }

    @action
    clearClipsProgressPositions() {
        this.clipsProgressPositions = {};
    }


    @action
    setNavigationRunningState(state: boolean) {
        this.isNavigationRunning = state;
    }

    @action
    setCurrentTimelineProgress(duration: number, position: number, movieId: number) {
        this.currentTimelineProgress.duration = duration;
        this.currentTimelineProgress.currentTime = position;
        this.currentTimelineProgress.movieId = Number(movieId);
    }


    @action
    setIsAnimationRunning(state: boolean) {
        this.isAnimationRunning = state;
    }

    @action
    setIsScrollAnimation(state: boolean) {
        this.isScrollAnimation = state;
    }

    @action
    setOffset() {
        this.checkOffset();

        if (this.scrollDirection === Direction.FORWARD) {
            this.currentOffset += -this.currentInnerHeight;
        } else if (this.scrollDirection === Direction.BACKWARD) {
            if (this.currentOffset >= 0) {
                return;
            }
            this.currentOffset += this.currentInnerHeight;
        }
    }

    @action
    moveOffsetBy(number: number) {
        this.currentOffset += number;
    }

    @action
    startItemsListBouncingAnimation() {
        if (this.itemsListBouncingAnimationInterval) {
            clearInterval(this.itemsListBouncingAnimationInterval);
        }

        this.itemsListBouncingAnimationInterval = setInterval(() => {
            this.isItemsListBounceAnimationDirectionForward = !this.isItemsListBounceAnimationDirectionForward;
            if (this.isItemsListBounceAnimationFirstBounce) {
                this.moveOffsetBy(-2 * OFFSET_FOR_BOUNCING_ANIMATION);
                this.isItemsListBounceAnimationFirstBounce = false;
            }

            if (this.itemsListBouncingAnimationIterationCount > ITEMS_LIST_BOUNCING_ANIMATION_MAX_ITERATION) {
                this.itemsListBouncingAnimationIterationCount = 0;
                this.clearItemsListBouncingAnimation();
            } else {
                this.itemsListBouncingAnimationIterationCount++;
            }


            this.moveOffsetBy(this.isItemsListBounceAnimationDirectionForward ? -OFFSET_FOR_BOUNCING_ANIMATION : OFFSET_FOR_BOUNCING_ANIMATION);
        }, 1000);
    }

    @action
    clearItemsListBouncingAnimation() {
        if (this.itemsListBouncingAnimationInterval) {
            clearInterval(this.itemsListBouncingAnimationInterval);
            this.itemsListBouncingAnimationInterval = null;
            this.setDefaultItemsListBouncingAnimationVariables();
        }
    }

    setDefaultItemsListBouncingAnimationVariables() {
        this.isItemsListBounceAnimationDirectionForward = false;
        this.isItemsListBounceAnimationFirstBounce = true;
        this.itemsListBouncingAnimationIterationCount = 0;
    }


    checkOffset() {
        if (!this.isOffsetCorrect()) {
            this.setIsScrollAnimation(false);
            this.repairOffset();
        } else {
            this.setIsScrollAnimation(true);
        }
    }

    isOffsetCorrect() {
        const left = -(this.currentInnerHeight * this.currentItemIndex);
        const right = this.currentOffset;

        return  left === right;
    }

    @action
    repairOffset() {
        let index;

        if (this.isAnimationRunning) {
            if (this.scrollDirection === Direction.FORWARD) {
                index = this.currentItemIndex + 1;
            } else {
                index = this.currentItemIndex === 0 ? 0 : this.currentItemIndex - 1;
            }
        } else {
            index = this.currentItemIndex;
        }
        const current = -(this.currentInnerHeight * index);
        this.currentOffset = current;
    }


    @action
    setOffsetTo(value: number) {
        this.currentOffset = value > 0 ? 0 : value;
    }

    @action
    setIsVisible(state: boolean) {
        this.isVisible = state;
    }

    setAnimationType(state: AnimationType) {
        this.animationType = state;
    }

    @action
    setIsLoadError(state: boolean) {
        this.isLoadError = state;
    }

    @action
    setOpenState(state: boolean) {
        this.isFullscreenOpen = state;
    }

    @action
    setShowToast(toastType: ToastType) {
        this.toastShow = true;
        this.toastType = toastType;

        if (this.toastTimeout != null) {
            clearTimeout(this.toastTimeout);
        }

        this.toastTimeout = setTimeout(() => {
            this.hideToast();
        }, DELETION_TIMEOUT);
    }

    @action
    hideToast() {
        this.toastShow = false;
    }

    @action
    setCurrentIndexByMovieId(movieId: number) {
        let index = this.rawFullscreenItems.findIndex((element) => {
            if (element.isClipUnavailable) {
                return false;
            }

            return element.meta.movie.movieId === movieId;
        });

        this.currentItemIndex = index || 0;
        this.updateCurrentFullscreenItems();
        this.updateHref();
    }

    @action
    setCurrentItemIndex(index: number) {
        this.currentItemIndex = index;
    }

    @action
    appendFirst(item: Clip, target: HTMLDivElement) {
        this.currentItemIndex = 0;

        if (item.meta) {
            this.initialClipMovieId = Number(item.meta.movie.id);
            this.initTarget = target;
        }

        this.rawFullscreenItems.unshift(item);

        this.updateCurrentFullscreenItems();
        this.setHasMore(true);
        this.setOffsetTo(0);
        this.linkHelper.pushCurrentUrlForSaveInBrowserHistory();

        this.updateHref();
    }

    @action
    setScroll(direction: Direction) {
        this.scrollDirection = direction;
    }

    @action
    saveCurrentClipPosition(timePercent: number, position: number) {
        const clip = this.rawFullscreenItems[this.currentItemIndex];

        if (!clip) {
            return;
        }

        const currentTime = this.currentTimelineProgress.currentTime;
        const duration = this.currentTimelineProgress.duration;

        const id = Number(clip.meta.movie.id);
        const save = this.clipsProgressPositions[id];
        if (save) {
            if (save.duration.toFixed(0) !== duration.toFixed(0)) {
                if (save.attempt < 2) {
                    save.attempt++;
                    return;
                }
            }
        }

        this.clipsProgressPositions[id] = {
            currentTime: currentTime,
            duration: duration,
            timePercent: timePercent,
            position: position,
            attempt: 0,
            movieId: id
        };
    }

    getCurrentClipPosition() {
        const clip = this.rawFullscreenItems[this.currentItemIndex];

        if (!clip) {
            return;
        }

        return this.clipsProgressPositions[clip.meta.movie.id];
    }

    getClipPosition(movieId: number) {
        return this.clipsProgressPositions[movieId];
    }

    @action
    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    @action
    appendItems(items: Clip[]) {
        items.forEach(newItem => this.appendItem(newItem));

        this.updateCurrentFullscreenItems();
    }

    private appendItem(item: Clip) {
        if (item.isClipUnavailable) {
            this.rawFullscreenItems.push(item);
        } else {
            const isDuplicate = this.rawFullscreenItems.some(existingItem => {
                if (existingItem.meta && item.meta) {
                    return existingItem.meta.movie.movieId === item.meta.movie.movieId;
                }
            });
            if (!isDuplicate) {
                this.rawFullscreenItems.push(item);
            }
        }
    }

    @action
    setSubscribed(state: boolean) {
        const item = this.currentFullscreenItems[this.currentItemIndex].item;
        if (!item) {
            return;
        }

        const id = item.payload.authorInfo.id;

        this.rawFullscreenItems.forEach((elem) => {
            if (elem.isClipUnavailable) {
                return false;
            }

            if (elem.payload.authorInfo.id === id) {
                elem.payload.authorInfo.isSubscribed = state;
            }
        });

        this.updateCurrentFullscreenItems();
    }


    @action
    doRequest(endpoint: CLIP_URLS) {
        switch (endpoint) {
            case CLIP_URLS.GET: {
                this.setIsLoading(true);

                let clip: Clip = this.rawFullscreenItems[this.rawFullscreenItems.length - 1];

                let marker: Marker;

                if (clip) {
                    marker = clip.marker;

                    if (!this.initialPlace && marker.source) {
                        this.initialPlace = marker.source;
                    }
                } else {
                    marker = {
                        isEmpty: 1
                    };
                }

                const req: WebApi<GetClipsResponse> = WebApi.request(CLIP_URLS.GET, { marker: marker, initialPlace: this.initialPlace });

                req.invoke().then(
                    resp => {
                        const clips = resp.clips;
                        if (resp && Array.isArray(clips)) {
                            this.appendItems(clips);
                            const lastClip = clips[clips.length - 1];

                            if (lastClip) {
                                this.setHasMore(lastClip.hasMore);
                            }
                        }
                    }
                ).finally(() => {
                    this.setIsLoading(false);
                });

                break;
            }
            case CLIP_URLS.LIKE:
            case CLIP_URLS.UNLIKE: {
                const clipInfo = this.getCurrentClipInfo();

                if (!clipInfo) {
                    return;
                }

                const req: WebApi<Response> = WebApi.request(
                    endpoint === CLIP_URLS.LIKE ? CLIP_URLS.LIKE : CLIP_URLS.UNLIKE,
                    {
                        ownerId: clipInfo.ownerId,
                        clipId: clipInfo.clipId,
                    }
                );

                req.invoke().then(
                    resp => {
                        if (resp.status === "OK") {

                        } else {

                        }
                    }
                );
                break;
            }

            case CLIP_URLS.SUBSCRIBE: {
                const clip = this.currentFullscreenItems[this.currentItemIndex];

                if (!(clip && clip.item)) {
                    return;
                }

                const owner = clip.item.payload.authorInfo;
                if (!owner.isOkOwner && owner.okId) {
                    return;
                }

                const req: WebApi<Response> = WebApi.request(CLIP_URLS.SUBSCRIBE, { ownerId: owner.okId, isGroup: owner.isUser ? 0 : 1 });

                req.invoke().then(
                    resp => {
                        if (resp.status === "OK") {
                            this.setShowToast(ToastType.SUBSCRIBED);
                        } else {

                        }
                    }
                );
                break;
            }
            case CLIP_URLS.RESHARE: {
                const clipInfo = this.getCurrentClipInfo();

                if (!clipInfo) {
                    return;
                }

                const req: WebApi<Response> = WebApi.request(CLIP_URLS.RESHARE, {
                    ownerId: clipInfo.ownerId,
                    clipId: clipInfo.clipId,
                });

                req.invoke().then(
                    resp => {
                        if (resp.status === "OK") {
                            this.setShowToast(ToastType.RESHARE_SUCCESS);
                        } else {

                        }
                    }
                );
                break;
            }
            default: {

            }
        }
    }

    @action
    setIndexToDelete() {
        if (this.isDeletionTimeoutRunning && this.deleteTimeout != null) {
            this.removeClip();
        }

        this.indexToDelete = this.currentItemIndex;
        this.startDelete();
    }

    startDelete() {
        this.isDeletionTimeoutRunning = true;
        this.deleteTimeout = setTimeout(() => {
            this.removeClip();
        }, DELETION_TIMEOUT);
    }

    @action
    removeClip() {
        if (this.isDeletionTimeoutRunning && this.deleteTimeout != null) {
            clearTimeout(this.deleteTimeout);
        }

        this.removeFullscreenItem(this.indexToDelete);

        this.indexToDelete = -1;
        this.isDeletionTimeoutRunning = false;
    }

    @action
    removeNotAllowed() {
        if (this.indexToDeleteNotAllowed == null) {
            return;
        }

        this.removeFullscreenItem(this.indexToDeleteNotAllowed);
        this.indexToDeleteNotAllowed = null;
        this.clearItemsListBouncingAnimation();
    }

    @action
    removeFullscreenItem(indexToDelete: number) {
        this.rawFullscreenItems.splice(indexToDelete, 1);

        if (this.currentItemIndex > indexToDelete) {
            this.currentItemIndex--;
        }

        this.updateCurrentFullscreenItems();
        this.checkOffset();
    }

    @action
    abortDeletion() {
        if (this.isDeletionTimeoutRunning && this.deleteTimeout) {
            clearTimeout(this.deleteTimeout);
        }
        this.hideToast();

        // Если мы уже на клипе, дизлайк которого отменяем, никаких анимаций делать не нужно
        if (this.indexToDelete === this.currentItemIndex) {
            this.indexToDelete = -1;
            this.isDeletionTimeoutRunning = false;
            return;
        }

        const isCurrentClipFurther = this.currentItemIndex > this.indexToDelete;

        const index = isCurrentClipFurther ? 1 : (-1);

        setTimeout(() => {
            this.indexToDelete = -1;
            this.isDeletionTimeoutRunning = false;
            this.setScroll(isCurrentClipFurther ? Direction.BACKWARD : Direction.FORWARD);
            this.setIsAnimationRunning(true);
            this.setOffset();
        }, 50);

        const item = this.rawFullscreenItems[this.indexToDelete + index];
        if (item !== null) {
            this.setCurrentIndexByMovieId(item.meta.movie.movieId);
            this.setIsAnimationRunning(false);
            this.checkOffset();
        }
    }

    @action
    setIsStopCurrentClip(state: boolean) {
        this.isStopCurrentClip = state;
    }

    @action
    updateCurrentFullscreenItems() {
        const leftIndex = this.currentItemIndex !== 0 ? this.currentItemIndex - 1 : 0;
        const rightIndex = this.currentItemIndex < this.rawFullscreenItems.length ? this.currentItemIndex + 1 : this.currentItemIndex;

        this.currentFullscreenItems = this.rawFullscreenItems.map((item, index) => {

            let result: FullScreenItem = {
                type: FullscreenItemType.CLIP,
                item: item,
                current: this.currentItemIndex === index,
                isNext: index - this.currentItemIndex === 1,
                isPrev: this.currentItemIndex - index === 1
            };

            if (item.isClipUnavailable) {
                result.type = FullscreenItemType.NOT_ALLOWED;
            }

            if (leftIndex <= index && index <= rightIndex) {
                result.isSkip = false;
            } else {
                result.isSkip = true;
                result.type = FullscreenItemType.STUB;
            }

            return result;
        });
    }


    @action
    setAnimateNotAllowedClip(state: boolean) {
        this.animateNotAllowedClip = state;
    }

    @action
    clearClips() {
        this.rawFullscreenItems = [];
    }

    updateHref() {
        const currentClipInfo = this.getCurrentClipInfo();

        if (!currentClipInfo) {
            return;
        }

        this.linkHelper.updateHref(UpdateType.UPDATE, currentClipInfo.clipId, currentClipInfo.ownerId);
    }

    getLinkOnCurrentClip1() {
        const currentClipInfo = this.getCurrentClipInfo();

        if (!currentClipInfo) {
            return this.linkHelper.path;
        }


        return this.linkHelper.getLinkOnCurrentClip1(currentClipInfo.ownerId, currentClipInfo.clipId);
    }

    getLinkOnCurrentClip() {
        const currentClipInfo = this.getCurrentClipInfo();

        if (!currentClipInfo) {
            return this.linkHelper.path;
        }


        return this.linkHelper.getLinkOnCurrentClip(currentClipInfo.ownerId, currentClipInfo.clipId);
    }

    getCurrentClipInfo(): ClipInfo | null {
        const item: FullScreenItem = this.currentFullscreenItems[this.currentItemIndex];

        if (!(item && item.item && item.type === FullscreenItemType.CLIP)) {
            return null;
        }

        const clipId = Number(item.item.meta.movie.id);
        const ownerId = item.item.payload.authorInfo.id;

        return {
            clipId,
            ownerId
        };
    }

    getRtuForAnonym() {
        const state = new URL(document.location.origin + this.anonymUrl).searchParams.get("st.cmd");
        return buildUrl(
            "/dk",
            {
                "st.cmd": state,
                "st.rtl": encodeURIComponent(this.getLinkOnCurrentClip())
            }
        );
    }


    @action
    setCurrentWindowInnerHeight() {
        if (this.isDesktop) {
            this.currentInnerHeight = window.innerHeight - DESKTOP_MARGIN_FROM_EDGE;
        } else {
            this.currentInnerHeight = window.innerHeight;
        }
    }

    setIsFullscreenClosedViaSystemBack(state: boolean) {
        this.isFullscreenClosedViaSystemBack = state;
    }

    setNeedToHandleSystemBack(state: boolean) {
        this.needToHandleSystemBack = state;
    }

    setIsDisableSystemBackHandling(state: boolean) {
        this.isDisableSystemBackHandling = state;
    }

    setUnionObserver(observer: IntersectionObserver) {
        this.unionObserver = observer;
    }
}
